import React from "react";
import {
  Autocomplete,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import { Venue } from "../../../../types/venue";
import CustomTextField from "../../../../components/ui/Textfield";
import type { IThemeState } from "../../../../types/theme";

interface VenueAutocompleteProps {
  value: Venue | null;
  onChange: (event: React.SyntheticEvent, value: Venue | null) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: string) => void;
  onAddVenueClick?: () => void;
  options: Venue[];
  loading?: boolean;
  label?: string;
  required?: boolean;
  gridSize?: number;
  serviceType?: string;
  theme: IThemeState;
  inputValue?: string;
}

const VenueAutocomplete: React.FC<VenueAutocompleteProps> = ({
  value,
  onChange,
  onInputChange,
  onAddVenueClick,
  options,
  loading = false,
  label = "Venues",
  required = false,
  gridSize = 12,
  serviceType,
  theme,
  inputValue,
}) => {
  return (
    <Grid size={{ xs: 12, md: gridSize }}>
      <Autocomplete
        fullWidth
        options={options}
        value={value}
        inputValue={inputValue}
        getOptionLabel={(option: Venue) => option.name || ""}
        onChange={onChange}
        onInputChange={onInputChange}
        loading={loading}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label={label}
            variant="outlined"
            required={required || serviceType === "CONFERENCE"}
            helperText="Type at least 3 characters to search venues"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: theme.primary_colors?.[400] || "#614438",
                      }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: theme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: theme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: theme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: theme.neutral_colors?.[100] || "#333",
              },
            }}
          />
        )}
      />

      {onAddVenueClick && (
        <IconButton
          onClick={onAddVenueClick}
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 1,
            color: theme.primary_colors?.[400] || "#614438",
            "&:hover": {
              backgroundColor: theme.neutral_colors?.[800] || "#f9f9f9",
            },
          }}
        >
          <AddCircleOutlineOutlined sx={{ mr: 0.5 }} />
          <Typography
            sx={{
              color: theme.neutral_colors?.[100] || "#333",
            }}
          >
            Add Venue
          </Typography>
        </IconButton>
      )}
    </Grid>
  );
};

export default VenueAutocomplete;
